import { useState } from 'react';
import { ScrollText, Mail } from 'lucide-react';
import { useApi } from '@/lib/useApi';
import { dateTime, humanise } from '@/lib/format';
import {
  Badge, Card, CardHeader, Checkbox, EmptyState, LoadingBlock, PageHeader, Tabs,
  statusTone,
} from '@/components/ui';
import type { AdminAuditRow, AdminEmailRow } from '@shared/types';

/**
 * What has been done, and what has been sent.
 *
 * Two logs on one screen because they answer the same kind of question — "what
 * happened to this account?" — from opposite ends.
 */
export default function Activity() {
  const [tab, setTab] = useState('audit');

  return (
    <>
      <PageHeader
        title="Activity"
        subtitle="Changes made from this console, and every email that left"
      />
      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[{ id: 'audit', label: 'Changes' }, { id: 'email', label: 'Email log' }]}
      />
      <div className="mt-6">
        {tab === 'audit' ? <AuditTab /> : <EmailTab />}
      </div>
    </>
  );
}

function AuditTab() {
  const [everyone, setEveryone] = useState(false);
  const { data, loading } = useApi<AdminAuditRow[]>('/admin/audit',
    everyone ? { all: 'true' } : undefined);

  return (
    <Card>
      <CardHeader
        title={everyone ? 'Everything' : 'Changes we made'}
        subtitle={everyone
          ? 'Including what customers did in their own accounts'
          : 'Actions taken from this console, newest first'}
        action={(
          <Checkbox
            label="Include customers"
            checked={everyone}
            onChange={(e) => setEveryone(e.target.checked)}
          />
        )}
      />
      {loading ? (
        <LoadingBlock rows={6} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={<ScrollText className="h-6 w-6" />}
          title="Nothing yet"
          message="Changes made from the console appear here."
        />
      ) : (
        <div className="divide-y divide-line">
          {data.map((row) => (
            <div key={row.id} className="px-5 sm:px-6 py-3 flex items-start gap-3">
              <Badge tone={row.actor_type === 'admin' ? 'info' : 'neutral'}>
                {row.actor_type}
              </Badge>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink">
                  <span className="font-medium">{row.actor_label}</span>
                  {' '}{humanise(row.action).toLowerCase()}
                  {row.entity ? ` — ${row.entity}` : ''}
                  {row.org_name ? ` · ${row.org_name}` : ''}
                </p>
                {row.detail != null && Object.keys(row.detail as object).length > 0 && (
                  <pre className="text-xs text-faint mt-1 whitespace-pre-wrap break-all
                                  font-mono leading-relaxed">
                    {JSON.stringify(row.detail)}
                  </pre>
                )}
              </div>
              <span className="text-xs text-faint shrink-0">{dateTime(row.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function EmailTab() {
  const { data, loading } = useApi<AdminEmailRow[]>('/admin/email/log');
  const [open, setOpen] = useState<string | null>(null);

  return (
    <Card>
      <CardHeader
        title="Email log"
        subtitle="Every message this deployment sent, including the ones that failed"
      />
      {loading ? (
        <LoadingBlock rows={6} />
      ) : !data || data.length === 0 ? (
        <EmptyState
          icon={<Mail className="h-6 w-6" />}
          title="Nothing sent yet"
          message="Password resets, welcome messages and anything you send from here."
        />
      ) : (
        <div className="divide-y divide-line">
          {data.map((row) => (
            <div key={row.id} className="px-5 sm:px-6 py-3">
              <button
                onClick={() => setOpen(open === row.id ? null : row.id)}
                className="w-full flex items-start gap-3 text-left"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-ink truncate">{row.subject}</p>
                  <p className="text-xs text-faint mt-0.5">
                    {row.to_email}
                    {row.org_name ? ` · ${row.org_name}` : ''}
                    {' · '}{humanise(row.purpose).toLowerCase()}
                    {' · '}{dateTime(row.created_at)}
                  </p>
                  {row.error_message && (
                    <p className="text-xs text-danger mt-1">{row.error_message}</p>
                  )}
                  {/*
                    What the provider said after handover. Absent entirely
                    until an event webhook is configured, which is why the
                    badge on the right still reports the handover itself.
                  */}
                  {(row.delivered_at || row.opened_at || row.bounced_at) && (
                    <p className="text-xs text-muted mt-1">
                      {[
                        row.bounced_at && `Bounced${row.bounce_reason ? ` — ${row.bounce_reason}` : ''}`,
                        !row.bounced_at && row.delivered_at && `Delivered ${dateTime(row.delivered_at)}`,
                        row.opened_at && `opened ${dateTime(row.opened_at)}${
                          (row.open_count ?? 0) > 1 ? ` (${row.open_count} times)` : ''}`,
                        row.clicked_at && 'link followed',
                      ].filter(Boolean).join(' · ')}
                    </p>
                  )}
                </div>
                <Badge tone={statusTone(row.status)}>{row.status}</Badge>
              </button>

              {open === row.id && row.body && (
                <pre className="mt-3 text-xs text-muted whitespace-pre-wrap leading-relaxed
                                bg-raised border border-line rounded-xl p-3">
                  {row.body}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
